// Admin Panel - Invoices Page
// File: admin-panel/src/pages/Invoices.jsx

import React, { useState, useEffect } from 'react';
import { collection, query, onSnapshot, orderBy, where } from 'firebase/firestore';
import { db } from '../../services/firebase'
import {
  DocumentTextIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  PrinterIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

function InvoiceModal({ invoice, isOpen, onClose }) {
  if (!isOpen || !invoice) return null;

  const formatCurrency = (amount) => `₹${(amount || 0).toLocaleString('en-IN')}`;
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-gray-800 border border-gray-700 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700 sticky top-0 bg-gray-800">
          <div>
            <h2 className="text-xl font-bold text-white">Invoice Details</h2>
            <p className="text-gray-400 text-sm">{invoice.invoiceNumber}</p>
          </div>
          <div className="flex items-center gap-2">
            <button className="p-2 rounded-lg hover:bg-gray-700 text-gray-400 hover:text-white">
              <PrinterIcon className="w-5 h-5" />
            </button>
            <button className="p-2 rounded-lg hover:bg-gray-700 text-gray-400 hover:text-white">
              <ArrowDownTrayIcon className="w-5 h-5" />
            </button>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-700 text-gray-400 hover:text-white">
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Invoice Header */}
          <div className="bg-gradient-to-r from-red-600/20 to-red-700/20 border border-red-500/30 rounded-xl p-4">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-white font-bold text-lg">DELIVERY INVOICE</h3>
                <p className="text-gray-400 text-sm">Generated: {formatDate(invoice.generatedAt)}</p>
              </div>
              <div className="text-right">
                <span className={`px-3 py-1 rounded-lg text-sm font-medium ${
                  invoice.status === 'completed' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                }`}>
                  {invoice.status?.toUpperCase() || 'COMPLETED'}
                </span>
              </div>
            </div>
          </div>

          {/* Customer & Porter Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-700/30 rounded-xl p-4">
              <h4 className="text-gray-400 text-sm font-medium mb-2">CUSTOMER</h4>
              <p className="text-white font-semibold">{invoice.customer?.name}</p>
              <p className="text-gray-400 text-sm">{invoice.customer?.phone}</p>
              <p className="text-gray-400 text-sm mt-2">{invoice.customer?.address}</p>
            </div>
            <div className="bg-gray-700/30 rounded-xl p-4">
              <h4 className="text-gray-400 text-sm font-medium mb-2">DELIVERED BY</h4>
              <p className="text-white font-semibold">{invoice.porter?.name || 'N/A'}</p>
              <p className="text-gray-400 text-sm">ID: {invoice.porter?.id || 'N/A'}</p>
              <p className="text-gray-400 text-sm mt-2">Completed: {formatDate(invoice.delivery?.completedAt)}</p>
            </div>
          </div>

          {/* Items */}
          <div className="bg-gray-700/30 rounded-xl p-4">
            <h4 className="text-gray-400 text-sm font-medium mb-3">ITEMS</h4>
            <div className="space-y-2">
              {invoice.items?.map((item, index) => (
                <div key={index} className="flex justify-between items-center py-2 border-b border-gray-600 last:border-0">
                  <div>
                    <p className="text-white font-medium">{item.name}</p>
                    <p className="text-gray-400 text-sm">Qty: {item.quantity} × {formatCurrency(item.unitPrice)}</p>
                  </div>
                  <p className="text-white font-semibold">{formatCurrency(item.totalPrice)}</p>
                </div>
              ))}
            </div>

            {/* Deselected Items */}
            {invoice.deselectedItems?.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-600">
                <h5 className="text-red-400 text-sm font-medium mb-2">NOT DELIVERED</h5>
                {invoice.deselectedItems.map((item, index) => (
                  <div key={index} className="flex justify-between items-center py-1">
                    <span className="text-gray-400">{item.name}</span>
                    <span className="text-red-400 text-sm">{item.reason}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Payment Summary */}
          <div className="bg-gray-700/30 rounded-xl p-4">
            <h4 className="text-gray-400 text-sm font-medium mb-3">PAYMENT SUMMARY</h4>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-400">Original Amount</span>
                <span className="text-white">{formatCurrency(invoice.payment?.originalAmount)}</span>
              </div>
              {invoice.payment?.discount > 0 && (
                <div className="flex justify-between">
                  <span className="text-red-400">Deductions</span>
                  <span className="text-red-400">-{formatCurrency(invoice.payment?.discount)}</span>
                </div>
              )}
              <div className="flex justify-between pt-2 border-t border-gray-600">
                <span className="text-white font-semibold">Amount Collected</span>
                <span className="text-green-400 font-bold text-lg">{formatCurrency(invoice.payment?.adjustedAmount)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Payment Mode</span>
                <span className={`font-medium ${
                  invoice.payment?.mode === 'cash' ? 'text-yellow-400' :
                  invoice.payment?.mode === 'online' ? 'text-blue-400' : 'text-green-400'
                }`}>
                  {invoice.payment?.mode?.toUpperCase()}
                </span>
              </div>
            </div>
          </div>

          {/* Delivery Photos */}
          {(invoice.delivery?.pickupPhotos?.length > 0 || invoice.delivery?.deliveryPhotos?.length > 0) && (
            <div className="bg-gray-700/30 rounded-xl p-4">
              <h4 className="text-gray-400 text-sm font-medium mb-3">PROOF PHOTOS</h4>
              <div className="grid grid-cols-4 gap-2">
                {invoice.delivery?.pickupPhotos?.map((photo, index) => (
                  <div key={`pickup-${index}`} className="relative">
                    <img src={photo} alt={`Pickup ${index + 1}`} className="w-full h-20 object-cover rounded-lg" />
                    <span className="absolute bottom-1 left-1 text-xs bg-green-500 text-white px-1 rounded">Pickup</span>
                  </div>
                ))}
                {invoice.delivery?.deliveryPhotos?.map((photo, index) => (
                  <div key={`delivery-${index}`} className="relative">
                    <img src={photo} alt={`Delivery ${index + 1}`} className="w-full h-20 object-cover rounded-lg" />
                    <span className="absolute bottom-1 left-1 text-xs bg-blue-500 text-white px-1 rounded">Delivery</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Grace Period Warning */}
          {invoice.completedInGracePeriod && (
            <div className="bg-yellow-500/20 border border-yellow-500/30 rounded-xl p-4">
              <p className="text-yellow-400 text-sm font-medium">
                ⚠️ This delivery was completed during the grace period
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [dateFilter, setDateFilter] = useState('all');

  useEffect(() => {
    const invoicesQuery = query(
      collection(db, 'invoices'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(invoicesQuery, (snapshot) => {
      const invoicesList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setInvoices(invoicesList);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const formatCurrency = (amount) => `₹${(amount || 0).toLocaleString('en-IN')}`;

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = dateStr.toDate ? dateStr.toDate() : new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const filteredInvoices = invoices.filter(invoice => {
    const matchesSearch = 
      invoice.invoiceNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      invoice.customer?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      invoice.porter?.name?.toLowerCase().includes(searchQuery.toLowerCase());

    if (dateFilter === 'all') return matchesSearch;

    const invoiceDate = invoice.generatedAt ? new Date(invoice.generatedAt) : new Date();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (dateFilter === 'today') {
      return matchesSearch && invoiceDate >= today;
    }
    if (dateFilter === 'week') {
      const weekAgo = new Date(today);
      weekAgo.setDate(weekAgo.getDate() - 7);
      return matchesSearch && invoiceDate >= weekAgo;
    }
    if (dateFilter === 'month') {
      const monthAgo = new Date(today);
      monthAgo.setMonth(monthAgo.getMonth() - 1);
      return matchesSearch && invoiceDate >= monthAgo;
    }

    return matchesSearch;
  });

  const totalAmount = filteredInvoices.reduce((sum, inv) => sum + (inv.payment?.adjustedAmount || 0), 0);
  const cashAmount = filteredInvoices
    .filter(inv => inv.payment?.mode === 'cash')
    .reduce((sum, inv) => sum + (inv.payment?.adjustedAmount || 0), 0);
  const onlineAmount = filteredInvoices
    .filter(inv => inv.payment?.mode === 'online')
    .reduce((sum, inv) => sum + (inv.payment?.adjustedAmount || 0), 0);

  const handleViewInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setShowModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-red-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Invoices</h1>
          <p className="text-gray-400 mt-1">View and manage delivery invoices</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
          <p className="text-gray-400 text-sm">Total Invoices</p>
          <p className="text-2xl font-bold text-white">{filteredInvoices.length}</p>
        </div>
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
          <p className="text-gray-400 text-sm">Total Amount</p>
          <p className="text-2xl font-bold text-green-400">{formatCurrency(totalAmount)}</p>
        </div>
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
          <p className="text-gray-400 text-sm">Cash Collections</p>
          <p className="text-2xl font-bold text-yellow-400">{formatCurrency(cashAmount)}</p>
        </div>
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
          <p className="text-gray-400 text-sm">Online Payments</p>
          <p className="text-2xl font-bold text-blue-400">{formatCurrency(onlineAmount)}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search invoices..."
            className="w-full pl-12 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500"
          />
        </div>
        <div className="flex gap-2">
          {['all', 'today', 'week', 'month'].map((filter) => (
            <button
              key={filter}
              onClick={() => setDateFilter(filter)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                dateFilter === filter
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              {filter === 'all' ? 'All' : filter.charAt(0).toUpperCase() + filter.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Invoice List */}
      <div className="bg-gray-800 border border-gray-700 rounded-2xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-700/50">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Invoice</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Customer</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Porter</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Amount</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Payment</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Date</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {filteredInvoices.length > 0 ? (
              filteredInvoices.map((invoice) => (
                <tr key={invoice.id} className="hover:bg-gray-700/30 transition-colors">
                  <td className="px-6 py-4">
                    <span className="text-red-400 font-semibold">{invoice.invoiceNumber}</span>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-white font-medium">{invoice.customer?.name}</p>
                    <p className="text-gray-400 text-sm">{invoice.customer?.phone}</p>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-white">{invoice.porter?.name || 'N/A'}</p>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-green-400 font-bold">{formatCurrency(invoice.payment?.adjustedAmount)}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-lg text-xs font-medium ${
                      invoice.payment?.mode === 'cash' ? 'bg-yellow-500/20 text-yellow-400' :
                      invoice.payment?.mode === 'online' ? 'bg-blue-500/20 text-blue-400' :
                      'bg-green-500/20 text-green-400'
                    }`}>
                      {invoice.payment?.mode?.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-gray-400">{formatDate(invoice.generatedAt)}</span>
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => handleViewInvoice(invoice)}
                      className="flex items-center gap-2 px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      <EyeIcon className="w-4 h-4" />
                      View
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7} className="px-6 py-12 text-center">
                  <DocumentTextIcon className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-400">No invoices found</p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Invoice Modal */}
      <InvoiceModal
        invoice={selectedInvoice}
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setSelectedInvoice(null);
        }}
      />
    </div>
  );
}

export default Invoices;